import { createRoot } from "react-dom/client";
import "../css/input.css";
import React, { lazy, Suspense, startTransition } from "react";

const App = lazy(() =>
  import(/* webpackChunkName: "app-calculator" */ "../components/App.jsx")
);
const AppChart = lazy(() =>
  import(/* webpackChunkName: "app-chart" */ "../components/AppChart.jsx")
);

/**
 * Advanced defer mechanism to prevent forced reflows during React hydration
 * Uses multiple strategies to ensure rendering happens outside of layout calculations
 */
const deferRenderWithReflowPrevention = (callback, priority = "low") => {
  // Strategy 1: Wait for any pending style/layout calculations to complete
  const afterStylesCalculated = (cb) => {
    // Force style recalculation to complete before our code runs
    if (document.body) {
      // Reading this forces any pending styles to flush
      void document.body.offsetHeight;
    }
    cb();
  };

  // Strategy 2: Use scheduler API if available (React 18+)
  if (typeof scheduler !== "undefined" && scheduler.postTask) {
    scheduler
      .postTask(() => afterStylesCalculated(callback), {
        priority: priority === "high" ? "user-blocking" : "background",
      })
      .catch(() => {
        // Fallback if scheduler fails
        requestIdleCallback(() => afterStylesCalculated(callback), {
          timeout: 200,
        });
      });
    return;
  }

  // Strategy 3: requestIdleCallback with increased timeout for complex pages
  if ("requestIdleCallback" in window) {
    requestIdleCallback(
      (deadline) => {
        // Only run if we have enough time, otherwise reschedule
        if (deadline.timeRemaining() > 10 || deadline.didTimeout) {
          afterStylesCalculated(callback);
        } else {
          // Not enough time, wait for next idle period
          requestIdleCallback(() => afterStylesCalculated(callback), {
            timeout: 300,
          });
        }
      },
      { timeout: 200 }
    );
    return;
  }

  // Strategy 4: Triple RAF to ensure we're past all layout calculations
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => afterStylesCalculated(callback));
    });
  });
};

/**
 * Batch DOM reads before React render to prevent layout thrashing
 * Also pre-warms DOMInterceptor cache if available
 */
const preRenderMeasurements = () => {
  // Pre-cache any layout values React might need
  const roots = {
    coins: document.getElementById("coins-root"),
    chart: document.getElementById("chart-root"),
  };

  // Use DOMInterceptor's batch read if available
  const batchRead = window.DOMInterceptor?.batchRead || ((cb) => cb());

  batchRead(() => {
    // Force layout calculation ONCE before React starts
    // This prevents React from triggering multiple reflows
    Object.values(roots).forEach((el) => {
      if (el) {
        // Pre-calculate dimensions so React doesn't need to
        void el.getBoundingClientRect();
        void el.offsetWidth;
        void el.offsetHeight;
      }
    });
  });

  // Pre-warm cache for critical elements
  if (window.DOMInterceptor?.prewarmCache) {
    const criticalElements = document.querySelectorAll(
      ".calculator, .coin-chart-container, .swiper-wrapper, .tab-content"
    );
    if (criticalElements.length > 0) {
      window.DOMInterceptor.prewarmCache(Array.from(criticalElements));
    }
  }

  return roots;
};

/**
 * Safe render function that wraps rendering in startTransition
 * to mark it as non-urgent (won't block user interactions)
 */
const safeRender = (root, component) => {
  startTransition(() => {
    root.render(component);
  });
};

// Wait for DOM to be ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initReact);
} else {
  // DOM already loaded, use microtask to defer slightly
  queueMicrotask(initReact);
}

function initReact() {
  // Pre-measure DOM elements to batch layout calculations
  const roots = preRenderMeasurements();

  // Render calculator component with reflow prevention
  if (roots.coins) {
    deferRenderWithReflowPrevention(() => {
      const root = createRoot(roots.coins);
      safeRender(
        root,
        <Suspense fallback={null}>
          <App />
        </Suspense>
      );
    }, "high");
  }

  // Render chart with additional delay to prevent concurrent layout reads
  if (roots.chart) {
    deferRenderWithReflowPrevention(() => {
      // Additional micro-delay for chart to ensure calculator is done
      setTimeout(() => {
        const root = createRoot(roots.chart);
        safeRender(
          root,
          <Suspense fallback={null}>
            <AppChart />
          </Suspense>
        );
      }, 50);
    }, "low");
  }
}
